import React from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import useSWR from 'swr'
import { Helmet } from 'react-helmet-async'
import { Stack, Box, Text, Image, Flex } from '@chakra-ui/react'
import { toast } from '@metropia/react-tools'
import H from '@here/maps-api-for-javascript/bin/mapsjs.bundle.harp'
import i18 from 'i18next'
import '@here/maps-api-for-javascript/bin/mapsjs-ui.css'

import * as api from 'src/libs/api'
import { Position } from 'src/libs/models'
import { useCurrentLocation } from 'src/libs/hooks'
import { getEnv } from 'src/libs/utils'
import { MapControl } from 'src/components'

import LOGO_METROPIA from 'src/assets/images/logo-metropia.png'

import { Context, MAP_ICON } from './constants'
import './here-ui.css'

function TripPlanning(props) {
  const platform = React.useRef(new H.service.Platform({ apikey: window.env.config.HERE_API_KEY }))
  // available langs: https://www.here.com/docs/bundle/map-tile-developer-guide/page/topics/resource-base-maptile.html
  const defaultLayers = React.useRef(platform.current.createDefaultLayers({ lg: 'cht', engineType: H.Map.EngineType.HARP }))

  const env = React.useMemo(() => getEnv(), [])
  const location = useLocation()
  const navigate = useNavigate()

  const {
    data: { data: version },
  } = useSWR(['version'], api.fetchTripServiceVersion, { revalidateOnFocus: false, fallbackData: { data: {} } })

  const {
    data: { data: trafficEvents },
  } = useSWR(['traffic/events'], api.fetchTrafficEvents, {
    revalidateOnFocus: false,
    fallbackData: { data: [] },
  })
  const currentTrafficEvents = React.useMemo(() => trafficEvents.filter((event) => event.endTime.getTime() > new Date().getTime()), [trafficEvents])

  const isCenteredRef = React.useRef(null)
  const mapContainerRef = React.useRef(null)
  const mapCtrlContainerRef = React.useRef(null)
  const mapRef = React.useRef(null)
  const uiRef = React.useRef(null)

  const currentLocation = useCurrentLocation()
  const isCurrentLocationValid = React.useMemo(() => Position.isValid(currentLocation), [currentLocation])

  const [mapMeta, setMapMeta] = React.useState({ center: env.DEFAULT_MAP_OPTION.center, zoom: env.DEFAULT_MAP_OPTION.zoom })

  /* initialize google and here maps */
  React.useEffect(() => {
    window.googleMapInstanceRef = new google.maps.Map(document.getElementById('google-map-for-sdk'))
  }, [])
  React.useLayoutEffect(() => {
    if (!mapContainerRef.current) return
    const map = new H.Map(mapContainerRef.current, defaultLayers.current.vector.normal.map, {
      ...env.DEFAULT_MAP_OPTION,
      engineType: H.Map.EngineType.HARP,
    })
    map.addLayer(defaultLayers.current.vector.traffic.map)

    const resizeMap = () => map.getViewPort().resize()
    window.addEventListener('resize', resizeMap)
    const onMapViewChangeEnd = (event) => setMapMeta({ center: event.target.getCenter(), zoom: event.target.getZoom() })
    map.addEventListener('mapviewchangeend', onMapViewChangeEnd)

    // eslint-disable-next-line no-new
    new H.mapevents.Behavior(new H.mapevents.MapEvents(map)) // default interactions for pan/zoom
    const ui = new H.ui.UI(map)

    uiRef.current = ui
    mapRef.current = map

    return () => {
      window.removeEventListener('resize', resizeMap)
      map.removeEventListener('mapviewchangeend', onMapViewChangeEnd)
      map.dispose()
    }
  }, [env])

  const moveToCurrentLocation = React.useCallback(() => {
    if (isCurrentLocationValid) {
      isCenteredRef.current = true
      mapRef.current.setZoom(env.DEFAULT_MAP_OPTION.zoom)
      mapRef.current.setCenter({ lat: currentLocation.lat, lng: currentLocation.lng })
    } else if (currentLocation.error) {
      toast({ status: 'error', description: i18.t('error.unauthenticated-location') })
    } else if (!isCurrentLocationValid && currentLocation.error === null) {
      toast({ status: 'warning', description: i18.t('warn.required-location') })
    }
  }, [env, isCurrentLocationValid, currentLocation.lat, currentLocation.lng, currentLocation.error])

  React.useEffect(() => {
    if (!isCenteredRef.current) {
      moveToCurrentLocation()
    }
  }, [moveToCurrentLocation])

  React.useEffect(() => {
    if (!isCurrentLocationValid) return

    const position = { lat: currentLocation.lat, lng: currentLocation.lng }
    const group = new H.map.Group()
    const mark = new H.map.Marker(position, { icon: MAP_ICON.CURRENT })
    const bubble = new H.ui.InfoBubble(position, { content: i18.t('common.current-location') })
    const ui = uiRef.current
    group.addObject(mark)
    ui.addBubble(bubble)
    mapRef.current.addObject(group)

    return () => {
      group.removeObject(mark)
      ui?.removeBubble(bubble)
    }
  }, [isCurrentLocationValid, currentLocation.lat, currentLocation.lng])

  React.useEffect(() => {
    if (location.pathname !== '/embedded/directions') return
    navigate(`/embedded${location.search}`, { replace: true })
  }, [navigate, location.pathname, location.search])

  return (
    <Context.Provider value={{ env, mapRef, mapMeta, uiRef, mapCtrlContainerRef, currentTrafficEvents }}>
      <Helmet>
        <title>{env.WEB_TITLE}</title>
        <meta property='og:title' content={env.WEB_TITLE} />
        <meta name='description' content={env.WEB_DESCRIPTION} />
      </Helmet>

      <Flex as='main'>
        <Box id='google-map-for-sdk' display='none' />
        <Box ref={mapContainerRef} flex='1' width='100%' height={window.innerHeight} />

        <Outlet />

        <Flex ref={mapCtrlContainerRef} direction='column' position='fixed' left='0' right='0' bottom='0' pointerEvents='none'>
          <Stack pointerEvents='auto' alignSelf='flex-end' align='flex-end' mb='1rem' px={{ base: '0.5rem', md: '1rem' }}>
            <MapControl map={mapRef.current} mapMeta={mapMeta} isTargetDisabled={!isCurrentLocationValid} onTarget={moveToCurrentLocation} />
            <Flex
              role='group'
              align='center'
              px='0.625rem'
              py='0.5rem'
              boxSize='2.5rem'
              bgColor='whiteAlpha.500'
              borderRadius='lg'
              shadow='md'
              transition='ease-in-out'
              _hover={{ width: '100%' }}
            >
              <Stack
                flex='1'
                direction='row'
                letterSpacing='-0.5px'
                spacing='0.25rem'
                align='center'
                color='gray.500'
                fontSize='xs'
                borderRightColor='gray.300'
                boxSize='0'
                opacity='0'
                visibility='hidden'
                _groupHover={{ mr: '0.5rem', pr: '0.5rem', borderRightWidth: '1px', boxSize: 'auto', opacity: 1, visibility: 'visible' }}
              >
                <Box sx={{ '> p': { display: 'inline-flex' } }}>
                  <Text>
                    Front-end: {process.env.REACT_APP_VERSION}-{env.VERSION}
                  </Text>
                  <Text mx='0.125rem'>/</Text>
                  <Text>API: {version.api}</Text>
                  <Text mx='0.125rem'>/</Text>
                  <Text>Engine: {version.engine}</Text>
                </Box>
                <Text>·</Text>
                <Text>{i18.t('common.copyright', { year: new Date().getFullYear() })}</Text>
                <Image src={LOGO_METROPIA} alt='logo-metropia' w='2rem' h='1.5rem' display={{ base: 'none', md: 'block' }} objectFit='cover' />
              </Stack>

              <Image src={require(`src/assets/images/${env.LOGO_NAME}`)} alt='logo' boxSize='1.25rem' />
            </Flex>
          </Stack>
        </Flex>
      </Flex>
    </Context.Provider>
  )
}

export default TripPlanning
